import clsx from 'clsx';
import { useNavigate } from 'react-router';
import { cnDate, fmtDateWeek, fmtListTime, fmtRelativeDay, type NotificationDTO } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { todayCN } from '../../lib/clock';
import { useLayout } from '../../lib/media';
import { useMarkNotificationsRead, useMe, useNotifications } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { Button, EmptyState, Skeleton } from '../../ui/primitives';
import './notifications.css';

// 站内通知：系统发给我的提醒（日报提醒、追问、指派、周报……），配了邮件服务时同步发邮件

export function NotificationsPage() {
  const mobile = useLayout() === 'mobile';
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: list, isLoading, error } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const today = todayCN();
  const unread = me?.unreadNotifications ?? list?.filter((n) => !n.read).length ?? 0;

  const open = (n: NotificationDTO) => {
    if (!n.read) markRead.mutate([n.id]);
    if (n.link) navigate(n.link);
  };

  // 按天分组
  const groups: { day: string; items: NotificationDTO[] }[] = [];
  for (const n of list ?? []) {
    const day = cnDate(new Date(n.createdAt));
    const g = groups[groups.length - 1];
    if (g?.day === day) g.items.push(n);
    else groups.push({ day, items: [n] });
  }

  const readAll =
    unread > 0 ? (
      <Button variant="text" mobile={mobile} disabled={markRead.isPending} onClick={() => markRead.mutate(undefined)}>
        全部标为已读
      </Button>
    ) : null;

  return (
    <>
      {!mobile && (
        <TopBar crumbs={['通知']} icon="bell">
          {readAll}
        </TopBar>
      )}
      <div className={clsx('ntf', mobile && 'mobile')}>
        {mobile && (
          <>
            <button type="button" className="ntf-back" onClick={() => navigate('/me')}>
              <Icon name="chevL" size={18} color="var(--text-3)" />
              <span>我</span>
            </button>
            <div className="ntf-title">
              <span className="flex1">通知</span>
              {readAll}
            </div>
          </>
        )}

        {isLoading ? (
          <div style={{ padding: mobile ? '12px 20px' : '12px 0' }} aria-busy="true">
            <Skeleton h={14} w={240} />
          </div>
        ) : error ? (
          <EmptyState icon="wifioff" title="通知没有加载出来" desc={errorText(error)} />
        ) : !list?.length ? (
          <EmptyState icon="bell" title="还没有通知" desc="日报提醒、追问、指派、周报等消息会出现在这里；配置了邮件的话，同时会发到你的工作邮箱。" />
        ) : (
          groups.map((g) => (
            <section key={g.day} className="ntf-group">
              <div className="ntf-day">{dayLabel(g.day, today)}</div>
              {g.items.map((n) => (
                <button key={n.id} type="button" className={clsx('ntf-row', !n.read && 'unread')} onClick={() => open(n)}>
                  <span className="dot" aria-label={n.read ? undefined : '未读'} />
                  <span className="col flex1" style={{ gap: 2, minWidth: 0 }}>
                    <span className="head">
                      <span className="label">{n.label}</span>
                      <span className="title ellipsis">{n.title}</span>
                    </span>
                    {n.body && <span className="body">{n.body}</span>}
                  </span>
                  <span className="time">{fmtListTime(n.createdAt, today)}</span>
                </button>
              ))}
            </section>
          ))
        )}
      </div>
    </>
  );
}

function dayLabel(day: string, today: string) {
  const rel = fmtRelativeDay(day, today);
  return rel === '今天' || rel === '昨天' ? `${rel} · ${fmtDateWeek(day)}` : fmtDateWeek(day);
}
